import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoadingPage } from '@/components/LoadingSpinner';
import { UploadDashboard } from '@/components/UploadDashboard';
import { DatabaseInstructionsPage } from '@/components/DatabaseInstructionsPage';
import { DevTools } from '@/components/DevTools';
import { getAppConfig } from '@/services/api';
import type { UploadResult, AppConfig } from '@/types';
import { UploadError } from '@/types';
import accelerateLogo from '/assets/accelerate-logo.png';

function App() {
  const { 
    isAuthenticated, 
    isLoading: authLoading, 
    error: authError, 
    token, 
    customerId,
    logout,
    setTokenFromDev 
  } = useAuth();

  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [currentPage, setCurrentPage] = useState<'upload' | 'instructions'>('upload');

  // Load application configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getAppConfig();
        setAppConfig({
          apiBaseUrl: window.CONFIG?.API_BASE_URL || '/api', // Use runtime config or fallback to proxy
          allowedFileTypes: config.allowedFileTypes,
          maxFileSize: config.maxFileSize,
          environment: config.environment as 'development' | 'production',
        });
      } catch (error) {
        console.error('Failed to load app config:', error);
        // Use default config if loading fails
        setAppConfig({
          apiBaseUrl: window.CONFIG?.API_BASE_URL || '/api', // Use runtime config or fallback to proxy
          allowedFileTypes: ['.sql', '.cs', '.java', '.py'],
          maxFileSize: 50 * 1024 * 1024,
          environment: import.meta.env.DEV ? 'development' : 'production',
        });
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, []);

  const handleUploadComplete = (results: UploadResult[]) => {
    setUploadResults(results);
    console.log('Upload completed:', results);
  };

  const handleUploadError = (error: UploadError) => {
    console.error('Upload error:', error);
  };

  const handleNavigateToInstructions = () => {
    setCurrentPage('instructions');
  };

  const handleNavigateToUpload = () => {
    setCurrentPage('upload');
  };

  // Show loading page while authentication or config is loading
  if (authLoading || configLoading) {
    return <LoadingPage message="Initializing application..." />;
  }

  // Show database instructions page (available to both authenticated and unauthenticated users)
  if (currentPage === 'instructions') {
    return (
      <DatabaseInstructionsPage 
        customerId={customerId || 'guest'} 
        onBack={handleNavigateToUpload}
      />
    );
  }

  // Show error page if authentication failed
  if (authError) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ backgroundColor: 'black' }}>
        <div className="max-w-md w-full bg-black text-white shadow-lg rounded-xl p-8" style={{ border: '2px solid var(--caylent-light-green)' }}>
          <div className="flex items-center mb-6">
            <div className="flex-shrink-0">
              <svg
                className="h-8 w-8"
                style={{ color: 'var(--caylent-purple)' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="caylent-small-title text-white" style={{ color: 'white' }}>
                Authentication Error
              </h3>
            </div>
          </div>
          
          <div className="mb-6">
            <p className="caylent-body mb-3 text-white" style={{ fontSize: '14px', color: 'white' }}>
              {authError}
            </p>
            <p className="caylent-body-small text-white" style={{ color: 'white', opacity: 0.7 }}>
              Please check your magic link or contact support for assistance.
            </p>
          </div>

          <button
            onClick={() => window.location.reload()}
            className="w-full text-white px-6 py-3 rounded-lg font-medium transition-all duration-200"
            style={{ 
              backgroundColor: 'var(--caylent-green)',
              fontSize: '14px'
            }}
            onMouseEnter={(e) => (e.target as HTMLButtonElement).style.backgroundColor = 'var(--caylent-dark-green)'}
            onMouseLeave={(e) => (e.target as HTMLButtonElement).style.backgroundColor = 'var(--caylent-green)'}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Show unauthenticated page
  if (!isAuthenticated || !token || !customerId) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ backgroundColor: 'black' }}>
        <div className="max-w-md w-full bg-black text-white shadow-lg rounded-xl p-8" style={{ border: '2px solid var(--caylent-light-green)' }}>
          <div className="text-center">
            <div className="mb-6">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                <img 
                  src={accelerateLogo} 
                  alt="Caylent Accelerate" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h2 className="caylent-medium-title mb-4 text-white" style={{ color: 'white' }}>
                Caylent Accelerate™ File Upload Portal
              </h2>
            </div>
            <p className="caylent-body mb-6 text-white" style={{ fontSize: '14px', color: 'white' }}>
              Please use the magic link provided via email by your Caylent account team to access the file upload portal.
            </p>
            <div className="caylent-body-small text-white" style={{ color: 'white', opacity: 0.7 }}>
              <p>If you don't have a magic link, please contact your Caylent account team.</p>
            </div>

            {/* Export Instructions for Unauthenticated Users */}
            <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--caylent-light-green)' }}>
              <div className="flex items-center justify-center mb-4">
                <div className="text-xl mr-2">📋</div>
                <h3 className="caylent-small-title text-white" style={{ color: 'var(--caylent-light-green)' }}>
                  Database Export Instructions
                </h3>
              </div>
              <p className="caylent-body-small text-white mb-4" style={{ fontSize: '12px', color: 'white', opacity: 0.8 }}>
                Need help exporting your database? View our step-by-step guides.
              </p>
              <button
                onClick={handleNavigateToInstructions}
                className="w-full px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{ 
                  backgroundColor: 'transparent',
                  border: '2px solid var(--caylent-green)',
                  color: 'var(--caylent-light-green)',
                  fontSize: '13px'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'var(--caylent-green)';
                  (e.target as HTMLButtonElement).style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLButtonElement).style.color = 'var(--caylent-light-green)';
                }}
              >
                📖 View Export Instructions
              </button>
            </div>
          </div>
        </div>
        
        {/* Development Tools */}
        {setTokenFromDev && (
          <DevTools onTokenGenerated={setTokenFromDev} />
        )}
      </div>
    );
  }

  // Main authenticated application
  // Show loading if we don't have appConfig yet but we're authenticated
  if (!appConfig) {
    return <LoadingPage message="Loading application configuration..." />;
  }

  return (
    <div className="min-h-screen bg-black text-white" style={{ backgroundColor: 'black' }}>
      {/* Header */}
      <header className="bg-black text-white shadow-sm" style={{ borderBottom: '2px solid var(--caylent-light-green)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <div className="w-12 h-12 mr-3 flex items-center justify-center">
                <img 
                  src={accelerateLogo} 
                  alt="Caylent Accelerate" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="caylent-small-title text-white" style={{ color: 'white' }}>
                File Upload Portal
              </h1>
              <span 
                className="ml-4 px-3 py-1 rounded-full caylent-body-small"
                style={{ 
                  backgroundColor: 'var(--caylent-light-green)', 
                  color: 'var(--caylent-dark-green)',
                  fontSize: '10px',
                  fontWeight: '500'
                }}
              >
                Customer: {customerId}
              </span>
            </div>
            <div className="flex items-center space-x-6">
              <button
                onClick={handleNavigateToInstructions}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200 text-black"
                style={{ 
                  backgroundColor: 'var(--caylent-light-green)',
                  color: 'var(--caylent-dark-green)',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'var(--caylent-green)';
                  (e.target as HTMLButtonElement).style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'var(--caylent-light-green)';
                  (e.target as HTMLButtonElement).style.color = 'var(--caylent-dark-green)';
                }}
              >
                📋 Export Instructions
              </button>
              <span className="caylent-body-small text-white" style={{ color: 'white', opacity: 0.7 }}>
                Environment: {appConfig.environment}
              </span>
              <button
                onClick={logout}
                className="caylent-body font-medium transition-colors duration-200 text-white"
                style={{ 
                  color: 'white',
                  fontSize: '12px'
                }}
                onMouseEnter={(e) => (e.target as HTMLButtonElement).style.color = 'var(--caylent-light-green)'}
                onMouseLeave={(e) => (e.target as HTMLButtonElement).style.color = 'white'}
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="bg-black text-white max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Export Instructions Call-to-Action */}
        <div className="mb-8 bg-gradient-to-r from-green-900/20 to-green-800/20 shadow-lg rounded-xl p-6" style={{ border: '2px solid var(--caylent-green)' }}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-3">
                <div className="text-2xl mr-3">📋</div>
                <h3 className="caylent-medium-title text-white" style={{ color: 'var(--caylent-light-green)' }}>
                  Need Help Exporting Your Database?
                </h3>
              </div>
              <p className="caylent-body text-white mb-4" style={{ fontSize: '14px', color: 'white', opacity: 0.9 }}>
                View step-by-step instructions for exporting your database schema using popular tools like DBeaver, MySQL Workbench, SQL Server Management Studio, and more.
              </p>
              <button
                onClick={handleNavigateToInstructions}
                className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 text-black"
                style={{ 
                  backgroundColor: 'var(--caylent-light-green)',
                  color: 'var(--caylent-dark-green)',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'var(--caylent-green)';
                  (e.target as HTMLButtonElement).style.color = 'white';
                  (e.target as HTMLButtonElement).style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'var(--caylent-light-green)';
                  (e.target as HTMLButtonElement).style.color = 'var(--caylent-dark-green)';
                  (e.target as HTMLButtonElement).style.transform = 'translateY(0)';
                }}
              >
                📖 View Export Instructions
              </button>
            </div>
            <div className="ml-4 text-6xl opacity-20">
              🛠️
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="caylent-large-title mb-3 text-white" style={{ color: 'white' }}>
            Upload Your Files
          </h2>
          <p className="caylent-body text-white" style={{ fontSize: '14px', color: 'white' }}>
            Upload {appConfig.allowedFileTypes.join(', ')} files up to{' '}
            {Math.round(appConfig.maxFileSize / 1024 / 1024)}MB each.
          </p>
        </div>

        {/* Upload Dashboard */}
        <div className="bg-black text-white shadow-lg rounded-xl p-8" style={{ border: '2px solid var(--caylent-light-green)' }}>
          <UploadDashboard
            token={token}
            customerId={customerId}
            allowedFileTypes={appConfig.allowedFileTypes}
            maxFileSize={appConfig.maxFileSize}
            onUploadComplete={handleUploadComplete}
            onUploadError={handleUploadError}
            className="w-full"
          />
        </div>

        {/* Upload Summary */}
        {uploadResults.length > 0 && (
          <div className="mt-8 bg-black text-white shadow-lg rounded-xl p-8" style={{ border: '2px solid var(--caylent-light-green)' }}>
            <div className="caylent-subtitle mb-2 text-white" style={{ color: 'white' }}>
              UPLOAD RESULTS
            </div>
            <h3 className="caylent-medium-title mb-6 text-white" style={{ color: 'white' }}>
              Upload Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--caylent-light-green)', border: '2px solid var(--caylent-green)' }}>
                <div className="caylent-large-title" style={{ color: 'var(--caylent-dark-green)' }}>
                  {uploadResults.filter(r => r.success).length}
                </div>
                <div className="caylent-body" style={{ color: 'var(--caylent-dark-green)', fontSize: '12px', fontWeight: '500' }}>Successful</div>
              </div>
              <div className="p-6 rounded-xl" style={{ backgroundColor: '#fef2f2', border: '2px solid #dc2626' }}>
                <div className="caylent-large-title" style={{ color: '#991b1b' }}>
                  {uploadResults.filter(r => !r.success).length}
                </div>
                <div className="caylent-body" style={{ color: '#991b1b', fontSize: '12px', fontWeight: '500' }}>Failed</div>
              </div>
              <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--caylent-light-purple)', border: '2px solid var(--caylent-purple)' }}>
                <div className="caylent-large-title" style={{ color: 'var(--caylent-purple)' }}>
                  {uploadResults.length}
                </div>
                <div className="caylent-body" style={{ color: 'var(--caylent-purple)', fontSize: '12px', fontWeight: '500' }}>Total</div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-black text-white mt-12" style={{ borderTop: '2px solid var(--caylent-light-green)' }}>
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="caylent-body-small text-white" style={{ color: 'white', opacity: 0.7 }}>
              Secure file upload powered by AWS S3 | Built with Caylent Accelerate
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Wrap App with ErrorBoundary
export default function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}